import React, { useEffect, useState, useCallback } from 'react';
import { Camera, RefreshCw, X, ExternalLink, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 7 * 60 * 1000; // mirror the backend FTP cadence

const LiveCameras = () => {
  const [data, setData] = useState({ count: 0, cameras: [], last_sync: null, attribution: null });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // { name, image_url }
  const [showAll, setShowAll] = useState(false);
  const [tick, setTick] = useState(0); // cache-buster for img src

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/traffic-cameras`, { cache: 'no-store' });
      const d = await r.json();
      setData(d);
    } catch { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { load(); setTick((t) => t + 1); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <section className="mt-12" data-testid="live-cameras-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  const prominent = data.cameras.filter((c) => c.prominent);
  const others    = data.cameras.filter((c) => !c.prominent);
  const visible   = showAll ? data.cameras : prominent;
  const att       = data.attribution || {};

  return (
    <section className="mt-12" data-testid="live-cameras-section">
      <header className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2" data-testid="cameras-h2">
            <Camera size={20} className="text-cyan-600" /> Live traffic cameras
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {data.count} cameras across the Renfrewshire and M8 / M77 corridor. Images refresh roughly every 5–10 minutes.
          </p>
        </div>
        <a href={att.link || 'https://www.traffic.gov.scot/'}
           target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 transition-colors"
           data-testid="traffic-scotland-attribution">
          Supplied by Traffic Scotland
          <ExternalLink size={11} />
        </a>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cameras-grid">
        {visible.map((c) => (
          <button
            key={c.id}
            onClick={() => setOpen(c)}
            className="group text-left rounded-lg overflow-hidden bg-slate-100 ring-1 ring-slate-200 hover:ring-cyan-400 hover:shadow-lg transition-all"
            data-testid={`camera-${c.image_name.replace('.jpg', '')}`}
          >
            <div className="relative aspect-[4/3] bg-slate-200">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}?t=${tick}`}
                alt={c.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
              />
              <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 bg-rose-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />Live
              </span>
            </div>
            <div className="p-2">
              <p className="text-xs font-semibold text-slate-900 truncate" title={c.name}>{c.name}</p>
            </div>
          </button>
        ))}
      </div>

      {!showAll && others.length > 0 && (
        <div className="text-center mt-4">
          <button
            onClick={() => setShowAll(true)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-cyan-700 transition-colors"
            data-testid="show-all-cameras-btn"
          >
            Show {others.length} more cameras
          </button>
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-4">
        {att.note} See <a href={att.link || 'https://www.traffic.gov.scot/'} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">traffic.gov.scot</a> for live traffic information.
      </p>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          data-testid="camera-lightbox"
        >
          <div
            className="relative max-w-4xl w-full bg-slate-900 rounded-xl overflow-hidden ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
              data-testid="camera-lightbox-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${open.image_url}?t=${tick}`}
              alt={open.name}
              className="w-full max-h-[75vh] object-contain bg-black"
            />
            <div className="p-4 text-white">
              <h3 className="text-lg font-bold inline-flex items-center gap-2">
                <MapPin size={16} className="text-cyan-400" /> {open.name}
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                {open.lat?.toFixed?.(4) ?? open.lat}, {open.lng?.toFixed?.(4) ?? open.lng} ·{' '}
                Supplied by{' '}
                <a href={att.link || 'https://www.traffic.gov.scot/'}
                   target="_blank" rel="noopener noreferrer"
                   className="underline hover:text-white">
                  Traffic Scotland
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LiveCameras;
